/*! IQ Max Truck Wash Locator */
import { Loader } from "googlemaps-js-api-loader";
import { getMapStyle } from "map-settings";

const EARTH_RADIUS_MI = 3958.8;

// viewBox is padded 1.5px on every side of the pin's own 32x42 geometry
// (via the <g translate>) specifically to give the white stroke room to
// render in full — the previous version's viewBox matched the path's
// bounds exactly, so half of that 1.5px stroke fell outside the viewBox and
// got hard-cropped along the top and both sides (the "flat spots" bug).
const PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="35" height="45" viewBox="0 0 35 45">
  <defs>
    <linearGradient id="twlPinGrad" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#6BA6FA"/>
      <stop offset="100%" stop-color="#1D52B8"/>
    </linearGradient>
    <radialGradient id="twlPinDotGrad" cx="35%" cy="30%" r="75%">
      <stop offset="0%" stop-color="#ffffff"/>
      <stop offset="100%" stop-color="#e7effe"/>
    </radialGradient>
  </defs>
  <g transform="translate(1.5 1.5)">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 11 16 26 16 26s16-15 16-26C32 7.163 24.837 0 16 0z" fill="url(#twlPinGrad)" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/>
    <circle cx="16" cy="16" r="6.5" fill="url(#twlPinDotGrad)"/>
  </g>
</svg>`;

// The classic Google/Apple Maps "you are here" dot — a soft halo behind a
// solid ringed dot, deliberately with no direction-facing cone/wedge.
const USER_PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
  <circle cx="20" cy="20" r="18" fill="#2f6fed" fill-opacity="0.16"/>
  <circle cx="20" cy="20" r="10" fill="#2f6fed" stroke="#ffffff" stroke-width="3"/>
</svg>`;

function haversineMiles(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MI * c;
}

function directionsUrl(lat, lng, label) {
  const ua = navigator.userAgent || "";
  const encodedLabel = encodeURIComponent(label || "");
  if (/iPad|iPhone|iPod/.test(ua)) {
    return `https://maps.apple.com/?daddr=${lat},${lng}&q=${encodedLabel}`;
  }
  if (/Android/.test(ua)) {
    return `geo:${lat},${lng}?q=${lat},${lng}(${encodedLabel})`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}

function formatAddress(loc) {
  const cityState = [loc.city, loc.state].filter(Boolean).join(", ");
  const cityStateZip = [cityState, loc.postalCode].filter(Boolean).join(" ");
  return [loc.street, cityStateZip].filter(Boolean).join(", ");
}

function websiteHref(url) {
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function escapeHtml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Hours are entered in a "Days: times | Days: times" convention (e.g.
// "Mon - Fri: 8am - 6pm | Sat: 8am - 4pm | Closed Sunday") so the day and
// time portions of each segment can be colored differently for faster
// scanning. A segment with no colon (e.g. "Open 24 hours", or a "Closed ..."
// segment) renders as a single day-styled span rather than splitting on
// nothing. Free text that doesn't follow the convention at all still
// displays correctly — it just renders as one plain segment, never breaks.
function formatHoursHtml(hoursStr) {
  const segments = hoursStr.split("|").map((s) => s.trim()).filter(Boolean);
  return segments
    .map((seg) => {
      const colonIdx = seg.indexOf(":");
      if (colonIdx === -1) {
        return `<span class="twl__hours-day">${escapeHtml(seg)}</span>`;
      }
      const day = seg.slice(0, colonIdx).trim();
      const time = seg.slice(colonIdx + 1).trim();
      return `<span class="twl__hours-day">${escapeHtml(day)}:</span> <span class="twl__hours-time">${escapeHtml(
        time
      )}</span>`;
    })
    .join(' <span class="twl__hours-sep">|</span> ');
}

const ICONS = {
  directions:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s7-7.58 7-12A7 7 0 0 0 5 10c0 4.42 7 12 7 12Z"></path><circle cx="12" cy="10" r="2.5"></circle></svg>',
  website:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z"></path></svg>',
  call: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z"></path></svg>',
  clock:
    '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3.5 2"></path></svg>',
};

// href/label are escaped here, once, regardless of what callers pass in —
// callers should always pass raw (unescaped) values.
function actionButton(kind, label, href, extraClass) {
  const compactClass = kind === "directions" ? "" : " twl__action-btn--compact";
  return `<a href="${escapeHtml(href)}" ${
    href.startsWith("tel:") ? "" : 'target="_blank" rel="noopener"'
  } class="c-btn ${extraClass} c-btn--small twl__action-btn${compactClass}" aria-label="${escapeHtml(label)}">${ICONS[kind]}<span>${escapeHtml(label)}</span></a>`;
}

const CLOSE_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"></path></svg>';

// Best-effort only: samples the photo's own top-right corner (behind the
// close icon) to decide whether the icon should render light or dark.
// Google's photo/Street View endpoints don't reliably send CORS headers, so
// this silently resolves null (caller keeps the safe default styling)
// whenever the canvas ends up tainted or the image fails to load — it never
// touches the actual displayed <img>, only a detached probe copy.
function detectIsLightCorner(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const probe = new Image();
    probe.crossOrigin = "anonymous";
    probe.onload = () => {
      try {
        const size = 24;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const sx = Math.max(0, probe.naturalWidth - size);
        ctx.drawImage(probe, sx, 0, size, size, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let total = 0;
        for (let i = 0; i < data.length; i += 4) {
          total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        }
        resolve(total / (data.length / 4) > 170);
      } catch (err) {
        resolve(null);
      }
    };
    probe.onerror = () => resolve(null);
    probe.src = url;
  });
}

class TruckWashLocator extends HTMLElement {
  #apiKey;
  #map;
  #infoWindow;
  #markers = new Map();
  #locations = [];
  #currentList = [];
  #userPosition = null;
  #activeId = null;
  #theme = "light";
  #userMarker = null;
  #routeLine = null;
  #directionsService = null;
  #routeRequestToken = null;

  connectedCallback() {
    this.#apiKey = this.getAttribute("api-key");
    this.defaultRadius = this.getAttribute("default-radius") || "any";
    this.mapStyle = this.getAttribute("map-style") || "default";

    this.form = this.querySelector("[data-twl-search-form]");
    this.locationInput = this.querySelector("[data-twl-location-input]");
    this.geoBtn = this.querySelector("[data-twl-geo-btn]");
    this.radiusSelect = this.querySelector("[data-twl-radius-select]");
    this.searchBtn = this.querySelector("[data-twl-search-btn]");
    this.listEl = this.querySelector("[data-twl-list]");
    this.seeMoreBtn = this.querySelector("[data-twl-see-more]");
    this.mapEl = this.querySelector("[data-twl-map]");
    this.resultsEl = this.querySelector(".twl__results");
    this.viewToggleBtns = this.querySelectorAll("[data-twl-view-btn]");
    this.listToggleBtn = this.querySelector("[data-twl-list-toggle]");
    this.statusEl = this.querySelector("[data-twl-status]");

    // The theme toggle and logo live outside <truck-wash-locator> itself
    // (siblings in the same section, so the toggle can be positioned
    // relative to the whole section rather than constrained to this
    // element's own box) — both are earlier in the DOM than this custom
    // element, so they're already parsed and queryable by the time this
    // callback runs.
    this.sectionEl = this.closest("section");
    this.themeToggleBtn = this.sectionEl ? this.sectionEl.querySelector("[data-twl-theme-toggle]") : null;
    this.themeToggleLabel = this.themeToggleBtn
      ? this.themeToggleBtn.querySelector("[data-twl-theme-toggle-label]")
      : null;
    this.logoEl = this.sectionEl ? this.sectionEl.querySelector(".twl__logo") : null;

    const dataScript = this.querySelector("[data-twl-locations]");
    try {
      this.#locations = JSON.parse(dataScript.textContent)
        .filter((loc) => loc.lat && loc.lng && !isNaN(parseFloat(loc.lat)) && !isNaN(parseFloat(loc.lng)))
        .map((loc) => ({ ...loc, lat: parseFloat(loc.lat), lng: parseFloat(loc.lng) }));
    } catch (e) {
      this.#locations = [];
    }

    if (this.radiusSelect && this.defaultRadius) {
      this.radiusSelect.value = this.defaultRadius;
    }

    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.handleSearch();
    });

    this.geoBtn.addEventListener("click", () => this.handleUseCurrentLocation());
    this.seeMoreBtn.addEventListener("click", () => this.expandList());
    this.viewToggleBtns.forEach((btn) => {
      btn.addEventListener("click", () => this.setMobileView(btn.dataset.twlViewBtn));
    });
    if (this.listToggleBtn) {
      this.listToggleBtn.addEventListener("click", () => this.toggleDesktopList());
    }

    this.#currentList = this.#locations;
    this.renderList(this.#locations, false);
    this.initMap();
    this.initAutocomplete();
    this.initThemeToggle();
    this.initResultsHeightSync();
  }

  // Desktop side-by-side layouts: sizes the list/map to fill the vertical
  // space so the gap left below them matches the gap above them (between
  // the search pill and the results), instead of a fixed height leaving a
  // large uneven gap at the bottom on tall viewports. Recomputed on load,
  // on resize (debounced — layout thrash on every resize tick is the same
  // mistake the map's own ResizeObserver had to avoid), and after a search
  // (the status message appearing/disappearing changes the top gap).
  initResultsHeightSync() {
    this.syncResultsHeight();
    // Custom heading/subheading web fonts can still be loading at first
    // paint and swap in later, shifting how tall the area above the results
    // is — re-measure once they've actually settled rather than trusting
    // the very first layout pass.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.syncResultsHeight());
    } else {
      window.setTimeout(() => this.syncResultsHeight(), 500);
    }
    let resizeTimer = null;
    window.addEventListener("resize", () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => this.syncResultsHeight(), 150);
    });
  }

  // Deliberately measured against the viewport, not the section's own box:
  // the section's height is intrinsic (it wraps its content, including the
  // results), so "space left before the section's bottom edge" is circular
  // — shrinking or growing the results by any amount just shrinks or grows
  // the section by the same amount, leaving zero space either way. What
  // Chad actually wants (list/map filling the screen, with the same gap
  // below them as above) only means something relative to the viewport,
  // which is an independent measurement.
  syncResultsHeight() {
    if (!this.resultsEl || !this.form) return;
    const layout = this.resultsEl.dataset.desktopLayout;
    if (this.isMobile() || (layout !== "side-by-side" && layout !== "side-by-side-inverse")) {
      this.resultsEl.style.removeProperty("--twl-results-height");
      return;
    }
    const formRect = this.form.getBoundingClientRect();
    const statusVisible = this.statusEl && !this.statusEl.hidden;
    const topAnchorBottom = statusVisible ? this.statusEl.getBoundingClientRect().bottom : formRect.bottom;
    const resultsRect = this.resultsEl.getBoundingClientRect();
    const topGap = resultsRect.top - topAnchorBottom;
    if (topGap <= 0) return;
    const available = window.innerHeight - resultsRect.top - topGap;
    if (!Number.isFinite(available)) return;
    // Column width doesn't depend on the row height we're about to set (grid
    // columns/rows size independently here), so it's safe to read now — used
    // to keep the map from reading as a tall, narrow strip on narrower
    // desktop widths, where a viewport-tall screen could otherwise hand it
    // way more height than its own width can support looking like a normal
    // map. Capping height relative to width keeps its shape sane at every
    // combination of viewport width and height.
    const mapColumn = this.resultsEl.querySelector(".twl__results-map");
    const mapWidth = mapColumn ? mapColumn.getBoundingClientRect().width : 0;
    const widthAspectCap = mapWidth > 0 ? mapWidth * 1.15 : Infinity;
    const clamped = Math.max(420, Math.min(available, widthAspectCap, 960));
    this.resultsEl.style.setProperty("--twl-results-height", `${Math.round(clamped)}px`);
  }

  // Address-prediction dropdown under the location input, powered by the
  // Places library. Selecting a suggestion gives us a lat/lng directly
  // (via place.geometry), so that path skips a separate Geocoding API call
  // entirely — only free-text searches (no suggestion picked) still use
  // geocodeAddress(). Best-effort: if Places isn't enabled on the API key
  // yet, this silently no-ops and plain text search keeps working.
  async initAutocomplete() {
    const ok = await this.ensureMapsLoaded();
    if (!ok) return;
    try {
      const { Autocomplete } = await window.google.maps.importLibrary("places");
      const autocomplete = new Autocomplete(this.locationInput, {
        fields: ["geometry", "formatted_address"],
        types: ["geocode"],
        componentRestrictions: { country: ["us", "ca"] },
      });
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place || !place.geometry || !place.geometry.location) return;
        this.#userPosition = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        if (place.formatted_address) this.locationInput.value = place.formatted_address;
        this.setStatus("");
        this.applyResults();
      });
      // Google's dropdown and our <form> both react to Enter — without
      // this, pressing Enter to pick a highlighted suggestion also submits
      // the form and fires a redundant/stale search. Swallow it only while
      // the suggestion list is actually open.
      this.locationInput.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;
        const pac = document.querySelector(".pac-container");
        if (pac && pac.offsetParent !== null && pac.childElementCount > 0) {
          e.preventDefault();
        }
      });
    } catch (err) {
      // Places not available — text search still works fully without it.
    }
  }

  isMobile() {
    return window.matchMedia("(max-width: 749px)").matches;
  }

  collapseListToActive() {
    this.listEl.classList.add("is-collapsed");
    this.seeMoreBtn.hidden = false;
  }

  // Segmented List/Map control: both buttons are always visible, so this
  // sets an explicit target view rather than toggling between two states.
  setMobileView(view) {
    if (this.resultsEl.dataset.mobileView === view) return;
    this.resultsEl.dataset.mobileView = view;
    this.viewToggleBtns.forEach((btn) => {
      const active = btn.dataset.twlViewBtn === view;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-selected", String(active));
    });

    if (view === "map" && this.#map) {
      // The map div was display:none an instant ago, so its last known
      // size is stale (often 0x0) — re-measure now that it's visible, and
      // switch to native single-finger pan since the map fully owns the
      // screen in this view (no page scroll left to protect).
      this.#map.setOptions({ gestureHandling: "greedy" });
      window.google.maps.event.trigger(this.#map, "resize");
      this.fitMapToResults(this.#currentList);
    }
  }

  expandList() {
    this.listEl.classList.remove("is-collapsed");
    this.seeMoreBtn.hidden = true;
  }

  // Desktop-only: collapses the list panel to width 0 so the map can use
  // the freed-up horizontal space. Only visible/relevant for the two
  // side-by-side desktop layouts (see CSS). The map's own debounced
  // ResizeObserver (see initMap) picks up the container-width change this
  // triggers and resyncs Google's map once the collapse animation settles —
  // no separate timeout needed here.
  toggleDesktopList() {
    const collapsed = this.resultsEl.dataset.listCollapsed === "true";
    const next = !collapsed;
    this.resultsEl.dataset.listCollapsed = String(next);
    this.listToggleBtn.setAttribute("aria-expanded", String(!next));
    this.listToggleBtn.setAttribute("aria-label", next ? "Expand list" : "Collapse list");
  }

  // Dark mode is scoped to this section only (see the [data-twl-theme] CSS
  // rules) and persisted per-browser in localStorage. Restoring a saved
  // preference on load does NOT persist again (persist=false) — it's
  // already in storage, re-writing it on every page load is pointless.
  initThemeToggle() {
    if (!this.themeToggleBtn) return;
    let stored = null;
    try {
      stored = window.localStorage.getItem("twl-theme");
    } catch (err) {
      // Storage can throw in locked-down browsing contexts — dark mode
      // just won't persist across visits there, nothing else breaks.
    }
    if (stored === "dark") this.setTheme("dark", false);
    this.themeToggleBtn.addEventListener("click", () => {
      this.setTheme(this.#theme === "dark" ? "light" : "dark", true);
    });
  }

  setTheme(theme, persist) {
    this.#theme = theme;
    if (this.sectionEl) this.sectionEl.dataset.twlTheme = theme;
    this.themeToggleBtn.setAttribute("aria-pressed", String(theme === "dark"));
    this.themeToggleBtn.setAttribute("aria-label", theme === "dark" ? "Switch to light mode" : "Switch to dark mode");
    if (this.themeToggleLabel) {
      this.themeToggleLabel.textContent = theme === "dark" ? "Light Mode" : "Dark Mode";
    }
    if (this.logoEl) {
      const src = theme === "dark" ? this.logoEl.dataset.darkSrc : this.logoEl.dataset.lightSrc;
      // The dark logo was already fetched via a low-priority <link
      // rel="preload"> in the page head, so this swap doesn't cost a
      // fresh network round-trip — it's already sitting in cache.
      if (src) this.logoEl.src = src;
    }
    if (this.#map) {
      this.#map.setOptions({ styles: getMapStyle(theme === "dark" ? "dark" : this.mapStyle) });
    }
    if (persist) {
      try {
        window.localStorage.setItem("twl-theme", theme);
      } catch (err) {
        // Same as above — persistence is a nice-to-have, not required.
      }
    }
  }

  setStatus(message, state) {
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = "";
      this.statusEl.removeAttribute("data-state");
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
    if (state) {
      this.statusEl.setAttribute("data-state", state);
    } else {
      this.statusEl.removeAttribute("data-state");
    }
  }

  async ensureMapsLoaded() {
    if (!this.#apiKey) {
      this.setStatus("Map is not configured. Please add a Google Maps API key to this section.", "error");
      return false;
    }
    if (!window.google || !window.google.maps) {
      try {
        const loader = new Loader({
          apiKey: this.#apiKey,
          version: "weekly",
          libraries: ["core", "maps", "marker", "geocoding", "places", "routes"],
        });
        await loader.importLibrary("core");
      } catch (err) {
        this.setStatus("The map couldn't be loaded right now. Please try again in a moment.", "error");
        return false;
      }
    }
    return true;
  }

  async initMap() {
    if (this.#map) return; // guard against double-init (e.g. theme editor re-render)
    const ok = await this.ensureMapsLoaded();
    if (!ok) return;

    // Give layout a moment to settle before ever measuring the container.
    // The mobile full-bleed map uses 100vw-based CSS, which can still be
    // resolving its final size in the same tick this script runs — if
    // Google Maps measures too early it bakes in a too-small viewport that
    // a later resize() trigger doesn't reliably fix. Deliberately a
    // setTimeout, not requestAnimationFrame: rAF can be paused indefinitely
    // in a backgrounded/non-visible tab, which would hang init forever —
    // setTimeout always fires (worst case, delayed) even then.
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    if (this.#map) return; // re-check: another call could have won the race

    try {
      const { Map, InfoWindow } = await window.google.maps.importLibrary("maps");
      const { Marker } = await window.google.maps.importLibrary("marker");

      const bounds = new window.google.maps.LatLngBounds();
      this.#locations.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));

      this.#map = new Map(this.mapEl, {
        zoom: 4,
        center: this.#locations.length ? bounds.getCenter() : { lat: 39.5, lng: -98.35 },
        zoomControl: true,
        disableDefaultUI: true,
        scrollwheel: true,
        gestureHandling: "cooperative",
        // this.#theme may already be "dark" here — initThemeToggle() runs
        // synchronously in connectedCallback right after this async
        // function's first await yields, so a restored localStorage
        // preference is applied before the map ever paints its first frame.
        styles: getMapStyle(this.#theme === "dark" ? "dark" : this.mapStyle),
      });

      this.#infoWindow = new InfoWindow();
      // Google re-renders fresh DOM into the InfoWindow every time it opens
      // (our own querySelector right after setContent()/open() would race
      // that), so wire the custom close button + run photo-brightness
      // detection from the 'domready' event instead, which fires exactly
      // once the new content is actually in the document.
      this.#infoWindow.addListener("domready", () => {
        const closeBtn = document.querySelector(".twl-infowindow__close");
        if (!closeBtn) return;
        closeBtn.addEventListener("click", () => this.#infoWindow.close());
        const photoUrl = closeBtn.dataset.photoUrl;
        if (photoUrl) {
          detectIsLightCorner(photoUrl).then((isLight) => {
            if (isLight !== null) closeBtn.classList.toggle("is-light", isLight);
          });
        }
      });

      const pinIcon = {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(PIN_SVG),
        scaledSize: new window.google.maps.Size(35, 45),
        anchor: new window.google.maps.Point(17.5, 43.5),
      };

      this.#locations.forEach((loc) => {
        const marker = new Marker({
          map: this.#map,
          position: { lat: loc.lat, lng: loc.lng },
          title: loc.name,
          icon: pinIcon,
        });
        marker.addListener("click", () => this.selectLocation(loc.id, "marker"));
        this.#markers.set(loc.id, marker);
      });

      this.fitMapToResults(this.#currentList);

      // Google's map only redraws its internal viewport when explicitly told
      // to — if the container's real size isn't settled yet at creation time
      // (web fonts loading, the mobile full-bleed 100vw layout resolving,
      // later reflows) the map renders undersized/off-center and never
      // corrects itself on its own. A ResizeObserver keeps it in sync with
      // its container for the page's whole lifetime, not just once at init.
      //
      // Debounced: the desktop list collapse/expand animates the map's
      // container width over ~350ms via a CSS transition, which fires this
      // observer dozens of times mid-animation. Calling Google's resize+fit
      // on every one of those intermediate frames was what caused the grey
      // flash — the map kept re-tiling against sizes it was already past.
      // Waiting until resize events stop for a beat means it fires exactly
      // once, against the final settled size. This is also now the single
      // place that resyncs the map after any container-size change — the
      // desktop collapse toggle no longer needs its own hardcoded timeout,
      // and this doubles as a safety net after the mobile view toggle's
      // immediate resize call, in case that ran before layout had settled.
      // Dim the map for the duration of a resize burst (see the .is-resizing
      // CSS rule) — Google re-tiles continuously while its container is
      // actively changing size, which is real, visible churn we can't
      // suppress outright, only mask behind a smooth fade instead of
      // showing it raw.
      let resizeSettleTimer = null;
      const resizeObserver = new ResizeObserver(() => {
        this.mapEl.classList.add("is-resizing");
        window.clearTimeout(resizeSettleTimer);
        resizeSettleTimer = window.setTimeout(() => {
          if (!this.#map) return;
          window.google.maps.event.trigger(this.#map, "resize");
          this.fitMapToResults(this.#currentList);
          this.mapEl.classList.remove("is-resizing");
        }, 150);
      });
      resizeObserver.observe(this.mapEl);
    } catch (err) {
      this.#map = null;
      this.setStatus("The map couldn't be loaded right now. Please try again in a moment.", "error");
    }
  }

  async geocodeAddress(address) {
    const { Geocoder } = await window.google.maps.importLibrary("geocoding");
    const geocoder = new Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          reject(status);
        }
      });
    });
  }

  async handleSearch() {
    const query = this.locationInput.value.trim();
    if (!query) {
      this.setStatus("Enter a city, state, or zip code to search.", "error");
      return;
    }
    this.setStatus("Searching…");
    this.searchBtn.disabled = true;
    try {
      const ok = await this.ensureMapsLoaded();
      if (!ok) return;
      this.#userPosition = await this.geocodeAddress(query);
      this.applyResults();
    } catch (err) {
      if (err === "OVER_QUERY_LIMIT" || err === "REQUEST_DENIED") {
        this.setStatus("Search is temporarily unavailable. Please try again later or call us directly.", "error");
      } else {
        this.setStatus("We couldn't find that location — try a different city, state, or zip.", "error");
      }
    } finally {
      this.searchBtn.disabled = false;
    }
  }

  handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      this.setStatus("Location services are not available in this browser.", "error");
      return;
    }
    this.geoBtn.classList.add("is-loading");
    this.setStatus("Getting your location…");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        this.#userPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.locationInput.value = "Current location";
        try {
          const ok = await this.ensureMapsLoaded();
          if (!ok) return;
          this.applyResults();
        } finally {
          this.geoBtn.classList.remove("is-loading");
        }
      },
      () => {
        this.geoBtn.classList.remove("is-loading");
        this.setStatus(
          "We couldn't access your location. Check your browser's location permissions and try again.",
          "error"
        );
      },
      { timeout: 10000 }
    );
  }

  applyResults() {
    if (!this.#userPosition) return;
    this.updateUserPositionMarker();
    this.clearRoute();
    const radiusValue = this.radiusSelect.value;
    const withDistance = this.#locations
      .map((loc) => ({
        ...loc,
        distance: haversineMiles(this.#userPosition.lat, this.#userPosition.lng, loc.lat, loc.lng),
      }))
      .sort((a, b) => a.distance - b.distance);

    const filtered =
      radiusValue === "any" ? withDistance : withDistance.filter((loc) => loc.distance <= parseFloat(radiusValue));

    if (filtered.length) {
      this.setStatus(
        `Showing ${filtered.length} location${filtered.length === 1 ? "" : "s"}${
          radiusValue === "any" ? "" : ` within ${radiusValue} miles`
        }, closest first.`
      );
      this.#currentList = filtered;
      this.renderList(filtered, true);
      this.fitMapToResults(filtered);
    } else {
      this.setStatus(`No IQ Max locations found within ${radiusValue} miles.`, "notice");
      this.#currentList = withDistance;
      this.renderList(withDistance, true);
      this.fitMapToResults(withDistance);
    }
    // The status message appearing/changing shifts the gap syncResultsHeight
    // measures from, so it needs a fresh read after every search — deferred
    // a tick so the browser has actually laid out the new status text first.
    window.setTimeout(() => this.syncResultsHeight(), 0);
  }

  // Places (or moves) the "you are here" dot. Called any time #userPosition
  // is newly set — a text search, an autocomplete pick, or "Use my
  // location" — so it always reflects the visitor's current search origin.
  updateUserPositionMarker() {
    if (!this.#map || !this.#userPosition) return;
    if (this.#userMarker) {
      this.#userMarker.setPosition(this.#userPosition);
      return;
    }
    this.#userMarker = new window.google.maps.Marker({
      map: this.#map,
      position: this.#userPosition,
      title: "Your location",
      zIndex: 999,
      icon: {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(USER_PIN_SVG),
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20),
      },
    });
  }

  // Real driving directions (Directions API), rendered as a thick rounded
  // line hugging the actual roads — this is a NEW billed Google API surface
  // (separate from Maps JS/Geocoding/Places), so it only fires on-demand
  // when a visitor actually clicks a list card on desktop, never on page
  // load. If the Directions API isn't enabled yet on this key, or the route
  // request fails for any reason, this quietly falls back to the previous
  // straight dashed line rather than showing nothing.
  async drawRouteToLocation(loc) {
    if (!this.#map || !this.#userPosition) return;
    this.clearRoute();
    const requestToken = (this.#routeRequestToken = Symbol());
    try {
      const { DirectionsService, DirectionsRenderer } = await window.google.maps.importLibrary("routes");
      if (!this.#directionsService) this.#directionsService = new DirectionsService();
      const result = await this.#directionsService.route({
        origin: this.#userPosition,
        destination: { lat: loc.lat, lng: loc.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      });
      // The visitor may have clicked a different card (or a map pin) while
      // this request was in flight — a stale response landing after a newer
      // click would otherwise draw the wrong route on top of/instead of the
      // current selection.
      if (requestToken !== this.#routeRequestToken || !this.#map) return;
      this.#routeLine = new DirectionsRenderer({
        map: this.#map,
        directions: result,
        suppressMarkers: true,
        preserveViewport: false,
        polylineOptions: {
          strokeColor: "#2f6fed",
          strokeOpacity: 0.92,
          strokeWeight: 6,
        },
      });
    } catch (err) {
      if (requestToken !== this.#routeRequestToken || !this.#map) return;
      this.drawFallbackLine(loc);
    }
  }

  // Straight dashed connecting line — used only when a real driving route
  // couldn't be fetched (Directions API not yet enabled on the key, request
  // failure, no result for the pair, etc.), so a visitor still sees roughly
  // where the location sits relative to them instead of nothing at all.
  drawFallbackLine(loc) {
    this.#routeLine = new window.google.maps.Polyline({
      map: this.#map,
      path: [this.#userPosition, { lat: loc.lat, lng: loc.lng }],
      strokeColor: "#2f6fed",
      strokeOpacity: 0,
      strokeWeight: 3,
      icons: [
        {
          icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 },
          offset: "0",
          repeat: "14px",
        },
      ],
    });
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(this.#userPosition);
    bounds.extend({ lat: loc.lat, lng: loc.lng });
    this.#map.fitBounds(bounds, 90);
  }

  clearRoute() {
    this.#routeRequestToken = Symbol();
    if (this.#routeLine) {
      this.#routeLine.setMap(null);
      this.#routeLine = null;
    }
  }

  fitMapToResults(list) {
    if (!this.#map || !list.length) return;
    if (list.length === 1) {
      this.#map.setCenter({ lat: list[0].lat, lng: list[0].lng });
      this.#map.setZoom(11);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    list.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));
    if (this.#userPosition) bounds.extend(this.#userPosition);
    this.#map.fitBounds(bounds, 60);
  }

  renderList(list, showDistance) {
    this.expandList();
    this.listEl.innerHTML = "";
    if (!list.length) {
      const li = document.createElement("li");
      li.className = "twl__list-empty";
      li.textContent = "No locations to show.";
      this.listEl.appendChild(li);
      return;
    }
    list.forEach((loc) => {
      const li = document.createElement("li");
      li.className = "twl__list-item";
      li.dataset.twlId = loc.id;
      if (loc.id === this.#activeId) li.classList.add("is-active");

      const address = formatAddress(loc);
      const distanceHtml =
        showDistance && typeof loc.distance === "number"
          ? `<span class="twl__list-item-distance">${loc.distance.toFixed(1)} mi</span>`
          : "";

      const photoUrl = this.photoUrl(loc);
      const thumbHtml = photoUrl
        ? `<img src="${escapeHtml(photoUrl)}" alt="${escapeHtml(
            loc.name
          )}" class="twl__list-item-thumb" loading="lazy" onerror="this.closest('.twl__list-item').classList.add('is-no-photo')">`
        : "";
      if (!photoUrl) li.classList.add("is-no-photo");

      const hoursHtml = loc.hours
        ? `<p class="twl__list-item-hours">${ICONS.clock}<span>${formatHoursHtml(loc.hours)}</span></p>`
        : "";

      // loc.logo is always a URL (either a merchant upload or the default
      // theme-asset naming convention — see the liquid template), not every
      // location actually has a file behind that convention, so a 404 there
      // is expected/normal, not an error worth surfacing as a broken image.
      const logoHtml = loc.logo
        ? `<img src="${escapeHtml(loc.logo)}" alt="" class="twl__list-item-logo" loading="lazy" onerror="this.remove()">`
        : "";

      // A location without its own Website URL falls back to its Google
      // Business Profile link instead — same button, same position, so a
      // visitor never sees a missing action just because a merchant hasn't
      // set up their own site yet.
      const websiteTarget = loc.website || loc.googleBusinessUrl;

      li.innerHTML = `
        <div class="twl__list-item-photo">${thumbHtml}</div>
        <div class="twl__list-item-content">
          <div class="twl__list-item-top">
            <div class="twl__list-item-title">
              ${logoHtml}
              <p class="twl__list-item-name">${escapeHtml(loc.name)}</p>
            </div>
            ${distanceHtml}
          </div>
          <address class="twl__list-item-address">${escapeHtml(address)}</address>
          ${hoursHtml}
        </div>
        <div class="twl__list-item-actions">
          ${actionButton("directions", "Directions", directionsUrl(loc.lat, loc.lng, loc.name), "c-btn--primary")}
          ${websiteTarget ? actionButton("website", "Website", websiteHref(websiteTarget), "c-btn--hollow") : ""}
          ${loc.phone ? actionButton("call", "Call", `tel:${loc.phone}`, "c-btn--hollow") : ""}
        </div>
      `;
      li.addEventListener("click", (e) => {
        if (e.target.closest("a")) return;
        this.selectLocation(loc.id, "list");
      });
      this.listEl.appendChild(li);
    });
  }

  // source: "marker" (clicked a pin on the map) always zooms in and opens
  // the popup — the "normal" behavior. "list" (clicked a card in the list)
  // does the same EXCEPT on desktop when we already know where the visitor
  // is: there, it draws a line to the location instead of zooming to it,
  // so the map keeps showing both points at once.
  selectLocation(id, source) {
    this.#activeId = id;
    this.listEl.querySelectorAll(".twl__list-item").forEach((el) => {
      el.classList.toggle("is-active", el.dataset.twlId === id);
    });
    if (this.isMobile()) this.collapseListToActive();
    const activeEl = this.listEl.querySelector(`[data-twl-id="${id}"]`);
    if (activeEl) activeEl.scrollIntoView({ block: "nearest", behavior: "smooth" });

    const loc = this.#locations.find((l) => l.id === id);
    const marker = this.#markers.get(id);
    if (!loc || !marker || !this.#map) return;

    const showRoute = source === "list" && !this.isMobile() && this.#userPosition;
    if (showRoute) {
      this.drawRouteToLocation(loc);
    } else {
      this.clearRoute();
      this.#map.setCenter(marker.getPosition());
      this.#map.setZoom(15);
    }

    const address = formatAddress(loc);
    const imageUrl = this.photoUrl(loc);
    const imageHtml = imageUrl
      ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(loc.name)}" class="twl-infowindow__image" loading="lazy" onerror="this.remove()">`
      : "";
    const hoursHtml = loc.hours
      ? `<p class="twl-infowindow__hours">${ICONS.clock}<span>${formatHoursHtml(loc.hours)}</span></p>`
      : "";
    this.#infoWindow.setContent(`
      <div class="twl-infowindow${this.#theme === "dark" ? " is-dark-theme" : ""}">
        <button type="button" class="twl-infowindow__close" data-photo-url="${escapeHtml(
          imageUrl
        )}" aria-label="Close">${CLOSE_ICON}</button>
        ${imageHtml}
        <div class="twl-infowindow__body">
          <p class="twl-infowindow__name">${escapeHtml(loc.name)}</p>
          <p class="twl-infowindow__address">${escapeHtml(address)}</p>
          ${hoursHtml}
          <div class="twl-infowindow__actions twl-infowindow__actions--full">
            ${actionButton("directions", "Directions", directionsUrl(loc.lat, loc.lng, loc.name), "c-btn--primary")}
          </div>
        </div>
      </div>
    `);
    // Google's InfoWindow auto-pans the map by default so the popup is
    // fully visible — normally harmless, but it actively fights the route
    // view: the destination marker usually sits right at the edge of the
    // just-computed route bounds, so autopan was silently re-centering the
    // map away from the "show both points" framing right after we set it
    // (confirmed by instrumenting map.setCenter — a second, unrequested
    // setCenter call landed ~600ms after the route's own fitBounds resolved
    // with the InfoWindow's own numbers, not ours). Disabled only for the
    // route case; the marker-click zoom-in case still benefits from the
    // normal autopan nudge.
    this.#infoWindow.setOptions({ disableAutoPan: showRoute });
    this.#infoWindow.open({ map: this.#map, anchor: marker });
  }

  // Deliberately no live-API fallback here (e.g. Street View Static) — both
  // photo fields are resolved once by an admin and baked into the block
  // settings, so rendering a location never fires a billed request. A
  // location with neither field just renders in its no-photo layout.
  photoUrl(loc) {
    return loc.image || loc.googlePhotoUrl || "";
  }
}

customElements.define("truck-wash-locator", TruckWashLocator);
